import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/expense/categories")({
  component: ExpenseCategoriesPage,
});

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { expenseCategorySchema } from "@/schemas/expense";
import { applyServerErrors } from "@/lib/apply-server-errors";
import { formatInOrgTimezone } from "@/lib/date-utils";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ExpenseCategoryTable } from "@/components/expense/tables/ExpenseCategoryTable";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, FileText, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import {
  useFetchExpenseCategories,
  useExpenseCategoryMutations,
  type ExpenseCategoryOrdering,
  type ExpenseCategoryOrderingField,
} from "@/hooks/api/expense/useExpenseCategories";
import type { ExpenseCategory } from "@/types/expense";
import type { ExpenseCategoryFormValues } from "@/schemas/expense";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

const STATUS_OPTIONS = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

function ExpenseCategoriesPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "expense.category.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 500);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<ExpenseCategory | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExpenseCategory | null>(
    null
  );

  const { ordering, toggleOrdering, getApiOrdering } = useDataTableOrdering<
    { name: SortDirection; created_at: SortDirection },
    ExpenseCategoryOrdering
  >({ name: null, created_at: null });

  const categoriesQuery = useFetchExpenseCategories({
    page_size: pageSize,
    page,
    ordering: getApiOrdering(),
    search: debouncedSearch || undefined,
    is_active: statusFilter === undefined ? undefined : statusFilter === "true",
  });
  const categories = categoriesQuery.data?.results ?? [];
  const count = categoriesQuery.data?.count ?? 0;

  const { createCategory, updateCategory, deleteCategory } =
    useExpenseCategoryMutations();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<ExpenseCategoryFormValues>({
    resolver: zodResolver(expenseCategorySchema),
    defaultValues: { name: "", is_active: true },
  });
  const watchedIsActive = watch("is_active");

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, setPage]);

  const isMutating =
    createCategory.isPending ||
    updateCategory.isPending ||
    deleteCategory.isPending;
  const isLoading = categoriesQuery.isPending || isMutating;
  const isFetching = categoriesQuery.isFetching || isMutating;

  const openCreate = () => {
    setEditingCategory(null);
    setIsViewOnly(false);
    reset({ name: "", is_active: true });
    setSheetOpen(true);
  };

  const openEdit = (cat: ExpenseCategory) => {
    setEditingCategory(cat);
    setIsViewOnly(false);
    reset({ name: cat.name, is_active: cat.is_active });
    setSheetOpen(true);
  };

  const openView = (cat: ExpenseCategory) => {
    setEditingCategory(cat);
    setIsViewOnly(true);
    reset({ name: cat.name, is_active: cat.is_active });
    setSheetOpen(true);
  };

  const handleFormSubmit = async (data: ExpenseCategoryFormValues) => {
    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({ id: editingCategory.id, payload: data });
        toast.success("Category updated");
      } else {
        await createCategory.mutateAsync(data);
        toast.success("Category created");
      }
      setSheetOpen(false);
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        expenseCategorySchema.keyof().options
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to save category");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCategory.mutateAsync({ id: deleteTarget.id });
      toast.success("Category deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleOrderingChange = (field: ExpenseCategoryOrderingField) => {
    toggleOrdering(field);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Expense Categories"
          description="Define the categories employees can select when submitting expenses."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={canManage ? openCreate : undefined}
          addLabel="New Category"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => {
            setStatusFilter(undefined);
            setSearchQuery("");
          }}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search categories..."
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <ExpenseCategoryTable
          categories={categories}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={handleOrderingChange}
          onView={openView}
          onEdit={openEdit}
          onDelete={(cat) => setDeleteTarget(cat)}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      {/* Create / Edit / View Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) setSheetOpen(false);
        }}
      >
        <SheetContent className="p-6">
          <SheetHeader className="p-0">
            <SheetTitle>
              {isViewOnly
                ? "Category Details"
                : editingCategory
                  ? "Edit Category"
                  : "New Category"}
            </SheetTitle>
            <SheetDescription>
              {isViewOnly
                ? "View category details."
                : editingCategory
                  ? "Update the category name or status."
                  : "Add a new expense category for your organization."}
            </SheetDescription>
          </SheetHeader>

          {isViewOnly && editingCategory ? (
            <div className="space-y-6 py-4">
              <div className="flex flex-col gap-1">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Name
                </Label>
                <p className="text-sm font-semibold pt-1 border-b pb-2">
                  {editingCategory.name}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  Status
                </Label>
                <Badge
                  variant={editingCategory.is_active ? "success" : "muted"}
                  className="capitalize px-3 py-1"
                >
                  {editingCategory.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Created At
                  </Label>
                  <p className="text-sm text-muted-foreground pt-1 border-b pb-2">
                    {formatInOrgTimezone(editingCategory.created_at, "MMM D, YYYY HH:mm")}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Updated At
                  </Label>
                  <p className="text-sm text-muted-foreground pt-1 border-b pb-2">
                    {formatInOrgTimezone(editingCategory.updated_at, "MMM D, YYYY HH:mm")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="mt-6 space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="cat-name">Name *</Label>
                <Input
                  id="cat-name"
                  placeholder="e.g. Travel, Meals, Fuel"
                  {...register("name")}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={watchedIsActive ? "true" : "false"}
                  onValueChange={(v) => setValue("is_active", v === "true")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSheetOpen(false)}
                  disabled={isMutating}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isMutating}>
                  {isMutating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {editingCategory ? "Save" : "Create"}
                </Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteCategory.isPending}
        title="Delete Category"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone. Existing line items using this category will retain the label.`}
      />
    </SidebarInset>
  );
}
